import React from 'react';
import styled, { css } from 'styled-components';
import { styles } from '../../../../styles/themes/style';

export type CampaignWizardStep = {
  id: string;
  label: string;
};

interface ICampaignWizardStepperProps {
  steps: CampaignWizardStep[];
  activeStepIndex: number;
  onStepClick?: (index: number) => void;
  /**
   * Allow clicking steps ahead of the active one. Used when editing an existing
   * campaign, where every step already holds saved values — the click handler still
   * validates each step it passes through. During creation only visited steps are
   * clickable, so the wizard stays a forward-only flow.
   */
  allowJumpAhead?: boolean;
}

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CampaignWizardStepper: React.FC<ICampaignWizardStepperProps> = ({
  steps,
  activeStepIndex,
  onStepClick,
  allowJumpAhead = false,
}) => (
  <StepperNav aria-label="Campaign creation progress">
    {steps.map((step, index) => {
      const isActive = index === activeStepIndex;
      const isCompleted = index < activeStepIndex;
      const clickable = !!onStepClick && (isCompleted || isActive || allowJumpAhead);

      return (
        <StepItem
          key={step.id}
          $reachedBefore={index <= activeStepIndex}
          $reachedAfter={index < activeStepIndex}
        >
          <StepButton
            type="button"
            $clickable={clickable}
            disabled={!clickable}
            onClick={() => clickable && onStepClick?.(index)}
            aria-current={isActive ? 'step' : undefined}
          >
            <StepCircle $active={isActive} $completed={isCompleted}>
              {isCompleted ? <CheckIcon /> : <span>{index + 1}</span>}
            </StepCircle>
            <StepLabel $active={isActive} $completed={isCompleted}>
              {step.label}
            </StepLabel>
          </StepButton>
        </StepItem>
      );
    })}
  </StepperNav>
);

export const CAMPAIGN_WIZARD_STEPS: CampaignWizardStep[] = [
  { id: 'settings', label: 'Campaign settings' },
  { id: 'recipients', label: 'Choose recipients' },
  { id: 'sequence', label: 'Outreach sequence' },
  { id: 'review', label: 'Campaign review' },
];

/** Wizard index for Campaign settings (name, lead source, execution). */
export const CAMPAIGN_WIZARD_STEP_SETTINGS = CAMPAIGN_WIZARD_STEPS.findIndex(s => s.id === 'settings');
/** Wizard index for lead entry (targeting, CSV, LD Contacts selection). */
export const CAMPAIGN_WIZARD_STEP_RECIPIENTS = CAMPAIGN_WIZARD_STEPS.findIndex(s => s.id === 'recipients');
/** @deprecated Use CAMPAIGN_WIZARD_STEP_RECIPIENTS */
export const CAMPAIGN_WIZARD_STEP_TARGETING = CAMPAIGN_WIZARD_STEP_RECIPIENTS;
export const CAMPAIGN_WIZARD_STEP_SEQUENCE = CAMPAIGN_WIZARD_STEPS.findIndex(s => s.id === 'sequence');
export const CAMPAIGN_WIZARD_STEP_REVIEW = CAMPAIGN_WIZARD_STEPS.findIndex(s => s.id === 'review');

const LINE_DONE = styles.colors.primary600;
const LINE_TODO = '#e4e8f0';
const CIRCLE = 30; // px

const StepperNav = styled.nav`
  display: flex;
  align-items: flex-start;
  gap: 0;
  width: 100%;
  overflow-x: auto;
  padding: 4px 0 6px;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const StepItem = styled.div<{ $reachedBefore: boolean; $reachedAfter: boolean }>`
  position: relative;
  flex: 1;
  min-width: 84px;
  display: flex;
  flex-direction: column;
  align-items: center;

  /* connector lines sit behind the circle, meeting at its centre */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: ${CIRCLE / 2}px;
    height: 2px;
    border-radius: 2px;
    z-index: 0;
    transition: background 0.25s ease;
  }
  &::before {
    left: 0;
    right: calc(50% + ${CIRCLE / 2 + 4}px);
    background: ${p => (p.$reachedBefore ? LINE_DONE : LINE_TODO)};
  }
  &::after {
    left: calc(50% + ${CIRCLE / 2 + 4}px);
    right: 0;
    background: ${p => (p.$reachedAfter ? LINE_DONE : LINE_TODO)};
  }
  &:first-child::before { display: none; }
  &:last-child::after { display: none; }
`;

const StepCircle = styled.span<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${CIRCLE}px;
  height: ${CIRCLE}px;
  border-radius: 50%;
  font-family: NunitoSans;
  font-size: 12px;
  font-weight: 800;
  box-sizing: border-box;
  transition: background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;

  ${p => {
    if (p.$active) {
      return css`
        background: ${styles.colors.primary600};
        color: ${styles.colors.white};
        border: 2px solid ${styles.colors.primary600};
      `;
    }
    if (p.$completed) {
      return css`
        background: ${styles.colors.primary100};
        color: ${styles.colors.primary600};
        border: 2px solid ${styles.colors.primary200};
      `;
    }
    return css`
      background: ${styles.colors.white};
      color: ${styles.colors.black300};
      border: 2px solid ${LINE_TODO};
    `;
  }}

  svg { display: block; }
`;

const StepButton = styled.button<{ $clickable: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0 6px;
  width: 100%;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};

  &:hover ${StepCircle} {
    ${p => p.$clickable && css`
      border-color: ${styles.colors.primary600};
    `}
  }
`;

const StepLabel = styled.span<{ $active: boolean; $completed: boolean }>`
  font-family: NunitoSans;
  font-size: 12.5px;
  font-weight: ${p => (p.$active ? 800 : 600)};
  line-height: 1.3;
  text-align: center;
  max-width: 116px;
  color: ${p =>
    p.$active ? styles.colors.primary600
    : p.$completed ? styles.colors.black500
    : styles.colors.black300};
  transition: color 0.2s ease;
`;

export default CampaignWizardStepper;
